import { useEffect, useRef } from 'react';

import {
  pickSnapTarget,
  releaseVelocity,
  snapDuration,
} from '@/components/calendar/hooks/column-snap';

const AXIS_SLOP_PX = 8;
/** A touch held this long before moving is a long-press (drag an event). */
const LONG_PRESS_MS = 200;

/**
 * Snappy column-to-column swiping for a horizontally scrolling grid on touch.
 *
 * The phone's own scroll-snap glides on its momentum and THEN settles, which
 * feels slow. This drives the scroll itself: the grid follows the finger 1:1,
 * and on release it is sent straight to the column the swipe was heading for in
 * ~150ms, so a flick lands on a column immediately.
 *
 * Only horizontal swipes are taken. `touch-action: pan-y` leaves vertical
 * scrolling to the browser, and a gesture that starts vertical is never
 * touched. A long-press (the calendar's drag-an-appointment gesture) is left
 * alone, so dragging an event sideways doesn't also scroll the grid.
 *
 * Native scroll-snap must be OFF on the element while this is enabled, or it
 * would fight every programmatic scroll.
 */
export function useColumnSnapScroll(
  element: HTMLElement | null,
  { enabled, step }: { enabled: boolean; step: number }
) {
  // The column width the scroll position was last aligned to. When the columns
  // are resized (rotate, window resize, split view), the pixel offset is left
  // pointing between two columns of the NEW width — a half column — so it is
  // re-derived from the column index rather than kept as pixels.
  const alignedStep = useRef(step);
  useEffect(() => {
    if (!element || !enabled || step <= 0) return;
    const previous = alignedStep.current;
    alignedStep.current = step;
    if (previous > 0 && previous !== step) {
      const index = Math.round(element.scrollLeft / previous);
      const max = Math.max(0, element.scrollWidth - element.clientWidth);
      element.scrollLeft = Math.min(index * step, max);
    }
  }, [element, enabled, step]);

  useEffect(() => {
    if (!element || !enabled || step <= 0) return;

    const previousTouchAction = element.style.touchAction;
    element.style.touchAction = 'pan-y';

    let frame = 0;
    let gesture: {
      startX: number;
      startY: number;
      startLeft: number;
      startTime: number;
      axis: 'x' | 'y' | null;
      samples: { t: number; x: number }[];
    } | null = null;

    const maxScroll = () => element.scrollWidth - element.clientWidth;
    const clamp = (value: number) =>
      Math.min(Math.max(value, 0), Math.max(0, maxScroll()));

    const onStart = (event: TouchEvent) => {
      cancelAnimationFrame(frame);
      if (event.touches.length !== 1) {
        gesture = null;
        return;
      }
      const touch = event.touches[0];
      gesture = {
        startX: touch.clientX,
        startY: touch.clientY,
        startLeft: element.scrollLeft,
        startTime: event.timeStamp,
        axis: null,
        samples: [{ t: event.timeStamp, x: touch.clientX }],
      };
    };

    const onMove = (event: TouchEvent) => {
      if (!gesture || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const dx = touch.clientX - gesture.startX;
      const dy = touch.clientY - gesture.startY;

      if (!gesture.axis) {
        if (Math.abs(dx) < AXIS_SLOP_PX && Math.abs(dy) < AXIS_SLOP_PX) return;
        const heldFirst = event.timeStamp - gesture.startTime > LONG_PRESS_MS;
        gesture.axis = !heldFirst && Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (gesture.axis !== 'x') return;

      element.scrollLeft = clamp(gesture.startLeft - dx);
      gesture.samples.push({ t: event.timeStamp, x: touch.clientX });
      if (gesture.samples.length > 12) gesture.samples.shift();
    };

    const onEnd = () => {
      const finished = gesture;
      gesture = null;
      if (!finished || finished.axis !== 'x') return;

      const to = pickSnapTarget({
        scrollLeft: element.scrollLeft,
        velocity: releaseVelocity(finished.samples),
        step,
        max: maxScroll(),
      });
      animateTo(to);
    };

    const onCancel = () => {
      gesture = null;
    };

    // Safety net: however a scroll ended (the browser took over a gesture and
    // cancelled ours, momentum, a programmatic jump), never rest between two
    // columns. Once scrolling has been still for a moment, settle onto the
    // nearest one.
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    const animateTo = (to: number) => {
      const from = element.scrollLeft;
      const duration = snapDuration(to - from);
      const startedAt = performance.now();
      const tick = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - (1 - progress) ** 3;
        element.scrollLeft = from + (to - from) * eased;
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };
    const onScroll = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (gesture) return;
        const target = pickSnapTarget({
          scrollLeft: element.scrollLeft,
          velocity: 0,
          step,
          max: maxScroll(),
        });
        if (Math.abs(target - element.scrollLeft) > 1) animateTo(target);
      }, 140);
    };

    element.addEventListener('touchstart', onStart, { passive: true });
    element.addEventListener('touchmove', onMove, { passive: true });
    element.addEventListener('touchend', onEnd, { passive: true });
    element.addEventListener('touchcancel', onCancel, { passive: true });
    element.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(idleTimer);
      element.style.touchAction = previousTouchAction;
      element.removeEventListener('touchstart', onStart);
      element.removeEventListener('touchmove', onMove);
      element.removeEventListener('touchend', onEnd);
      element.removeEventListener('touchcancel', onCancel);
      element.removeEventListener('scroll', onScroll);
    };
  }, [element, enabled, step]);
}
